import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { MapPin, Handshake, Calculator, Bell, AlertTriangle, Phone, FileText, TrendingUp, Clock, ArrowRight, Building2, X, Loader2, Edit2 } from 'lucide-react';
import CoverageHeatmap from '../components/app/CoverageHeatmap';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine, Line, ComposedChart, Legend } from 'recharts';
import { useSyncListener } from '../hooks/useDataSync';
import { groupByProvince } from '../utils/portfolioSort';
import { ColumnPicker, useVisibleColumns } from '../components/app/ColumnPicker';

const _eur0 = (n) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);

// Columnas seleccionables para el drilldown del forecast.
const FORECAST_COLUMNS = [
  { key: 'code', label: 'Código', defaultVisible: true, always: true,
    render: (s) => (
      <td className="px-3 py-2 font-mono text-blue-200" key="code">
        {s.code || s.name || '—'}
        {s.alt_code && <div className="text-white/40 text-[10px]">{s.alt_code}</div>}
      </td>
    ),
  },
  { key: 'address', label: 'Dirección', defaultVisible: true,
    render: (s) => <td className="px-3 py-2 text-white/70 max-w-xs truncate" title={s.address} key="a">{s.address || '—'}</td>,
  },
  { key: 'muni_prov', label: 'Municipio · Provincia', defaultVisible: true,
    render: (s) => <td className="px-3 py-2 text-white/70" key="m">{s.municipality || '—'} <span className="text-white/40">· {s.province || '—'}</span></td>,
  },
  { key: 'owner', label: 'Propietario', defaultVisible: true,
    render: (s) => (
      <td className="px-3 py-2 text-white/70" key="o">
        {s.owner_name || <span className="text-white/30">—</span>}
        {s.owner_phone && <div className="text-white/40 text-[10px]">📞 {s.owner_phone}</div>}
      </td>
    ),
  },
  { key: 'rent', label: 'Renta €/año', defaultVisible: true, align: 'right',
    render: (s) => <td className="px-3 py-2 text-right font-black text-[#A3FF12]" key="r">{s.current_rent ? _eur0(s.current_rent) : '—'}</td>,
  },
  { key: 'contract_end', label: 'Vence', defaultVisible: true,
    render: (s) => <td className="px-3 py-2 text-white/70" key="v">{s.contract_end?.slice(0,10) || '—'}</td>,
  },
  { key: 'contract_signed_at', label: 'Firma', defaultVisible: false,
    render: (s) => <td className="px-3 py-2 text-white/70" key="f">{s.contract_signed_at?.slice(0,10) || '—'}</td>,
  },
  { key: 'contract_type', label: 'Tipo contrato', defaultVisible: false,
    render: (s) => <td className="px-3 py-2 text-white/70 text-[11px]" key="t">{s.contract_type || '—'}</td>,
  },
  { key: 'fianza', label: 'Fianza (€)', defaultVisible: false, align: 'right',
    render: (s) => <td className="px-3 py-2 text-right text-white/70" key="fz">{s.fianza_eur ? _eur0(s.fianza_eur) : '—'}</td>,
  },
  { key: 'superficie', label: 'Superficie m²', defaultVisible: false, align: 'right',
    render: (s) => <td className="px-3 py-2 text-right text-white/70" key="sup">{s.superficie_m2 ? new Intl.NumberFormat('es-ES').format(s.superficie_m2) : '—'}</td>,
  },
  { key: 'catastro', label: 'Ref. catastral', defaultVisible: false,
    render: (s) => <td className="px-3 py-2 font-mono text-[11px] text-white/60" key="cat">{s.catastro_ref || '—'}</td>,
  },
  { key: 'contract_ref', label: 'Ref. contrato', defaultVisible: false,
    render: (s) => <td className="px-3 py-2 font-mono text-[11px] text-white/60" key="cref">{s.contract_ref || '—'}</td>,
  },
];

const StatCard = ({ icon: Icon, label, value, sub, accent }) => (
  <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-4 lg:p-6 hover:border-[#A3FF12]/30 transition-colors">
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 lg:mb-4 ${accent ? 'bg-[#A3FF12]/10 text-[#A3FF12]' : 'bg-white/5 text-white/70'}`}>
      <Icon size={20}/>
    </div>
    <div className="text-2xl lg:text-3xl font-black text-white">{value}</div>
    <div className="text-white/70 text-xs lg:text-sm font-semibold">{label}</div>
    {sub && <div className="text-white/40 text-xs mt-1 hidden sm:block">{sub}</div>}
  </div>
);

const PriorityBadge = ({ p }) => {
  const map = { L1: 'bg-red-500/10 text-red-400 border-red-500/30', L2: 'bg-orange-500/10 text-orange-400 border-orange-500/30', L3: 'bg-white/5 text-white/60 border-white/10' };
  return <span className={`text-xs font-bold px-2 py-1 rounded-full border ${map[p] || map.L3}`}>{p}</span>;
};

const Dashboard = () => {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [negotiations, setNegotiations] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [horizon, setHorizon] = useState(24);
  const [drilldown, setDrilldown] = useState(null);  // { month, loading, data }
  const [drillCols, setDrillCols] = useVisibleColumns(FORECAST_COLUMNS, 'forecast.drilldown.cols.v1');
  const activeDrillCols = useMemo(() => FORECAST_COLUMNS.filter((c) => drillCols.has(c.key)), [drillCols]);
  const drillColSpan = activeDrillCols.length + 1;
  const navigate = useNavigate();

  const openDrilldown = async (month) => {
    setDrilldown({ month, loading: true, data: null });
    try {
      const { data } = await api.get(`/sites/expiring/${month}`);
      setDrilldown({ month, loading: false, data });
    } catch (e) {
      setDrilldown({ month, loading: false, error: e?.response?.data?.detail || e.message });
    }
  };

  const load = useCallback(async () => {
    try {
      const [s, n, a, f] = await Promise.all([
        api.get('/dashboard/summary'),
        api.get('/negotiations'),
        api.get('/alerts'),
        api.get(`/dashboard/forecast-expirations?months=${horizon}`),
      ]);
      setSummary(s.data);
      setNegotiations(n.data.slice(0, 6));
      setAlerts(a.data.slice(0, 8));
      setForecast(f.data);
    } catch (e) { console.error(e); }
  }, [horizon]);
  useEffect(() => { load(); }, [load]);
  // Auto-refresh cuando el backend indica que algo cambió.
  useSyncListener(load, ['sites', 'negotiations', 'incidents', 'alerts', 'agenda']);

  const markRead = async (id) => {
    await api.post(`/alerts/${id}/read`);
    load();
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-xs font-bold tracking-widest text-[#A3FF12] mb-2">PANEL DE CONTROL</div>
          <h1 className="text-3xl lg:text-4xl font-black text-white">Hola, {user?.name?.split(' ')[0]}. 👋</h1>
          <p className="text-white/50 mt-2 text-sm lg:text-base">Resumen de tus emplazamientos, negociaciones y avisos.</p>
        </div>
        <Link to="/app/prospeccion" className="inline-flex items-center gap-2 bg-[#A3FF12] text-black px-5 py-3 rounded-full font-bold hover:bg-[#b8ff3a] transition-colors text-sm lg:text-base">
          Prospectar zona <ArrowRight size={18}/>
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-8 lg:mb-10">
        <StatCard icon={MapPin} label="Emplazamientos" value={summary?.total_sites ?? '–'} sub="Total registrados" accent/>
        <StatCard icon={Handshake} label="Negociaciones activas" value={summary?.active_negotiations ?? '–'} sub="Abiertas + pendientes"/>
        <StatCard icon={Clock} label="Vencen en 180 días" value={summary?.upcoming_expirations_30d ?? '–'} sub="Contratos próximos" accent/>
        <StatCard icon={Bell} label="Avisos sin leer" value={summary?.unread_alerts ?? '–'} sub="Acciones pendientes"/>
      </div>

      {/* Forecast rolling vencimientos */}
      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-4 lg:p-6 mb-8 lg:mb-10" data-testid="forecast-expirations">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <div className="text-[10px] font-black tracking-widest text-[#A3FF12]">FORECAST · VENCIMIENTOS DE CONTRATO</div>
            <h2 className="text-lg lg:text-xl font-black mt-1">Próximos {horizon} meses — {forecast?.totals?.count ?? 0} contratos · {new Intl.NumberFormat('es-ES',{style:'currency',currency:'EUR',maximumFractionDigits:0}).format(forecast?.totals?.rent_annual || 0)}/año en riesgo</h2>
            <p className="text-white/50 text-xs mt-1">Pulsa una barra para ver los emplazamientos que vencen ese mes con contactos, direcciones y rentas.</p>
          </div>
          <div className="flex gap-1 bg-black/40 border border-white/10 rounded-lg overflow-hidden">
            {[6, 12, 24, 36].map((m) => (
              <button key={m} onClick={() => setHorizon(m)} data-testid={`forecast-btn-${m}m`}
                className={`px-3 py-1.5 text-xs font-bold ${horizon === m ? 'bg-[#A3FF12] text-black' : 'text-white/60 hover:text-white'}`}>
                {m}m
              </button>
            ))}
          </div>
        </div>
        {forecast && forecast.series && forecast.totals.count > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={forecast.series} margin={{ top: 8, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#222"/>
              <XAxis dataKey="month" tick={{ fill: '#888', fontSize: 10 }} interval={horizon > 12 ? 1 : 0}/>
              <YAxis yAxisId="l" tick={{ fill: '#888', fontSize: 11 }}/>
              <YAxis yAxisId="r" orientation="right" tick={{ fill: '#888', fontSize: 11 }} tickFormatter={(v) => v > 1e6 ? `${(v/1e6).toFixed(1)}M€` : `${(v/1e3).toFixed(0)}k€`}/>
              <Tooltip contentStyle={{ background: '#111', border: '1px solid #333', borderRadius: 8 }}
                       formatter={(v, n) => n === 'count' ? [`${v} contratos · click`, 'Vencimientos'] : [new Intl.NumberFormat('es-ES',{style:'currency',currency:'EUR',maximumFractionDigits:0}).format(v), 'Renta anual']}/>
              <Legend wrapperStyle={{ fontSize: 11 }}/>
              <ReferenceLine yAxisId="l" x={forecast.series[0]?.month} stroke="#A3FF12" strokeDasharray="3 3" label={{ value: 'Hoy', position: 'top', fill: '#A3FF12', fontSize: 10 }}/>
              <Bar yAxisId="l" dataKey="count" name="Contratos" fill="#A3FF12" radius={[4, 4, 0, 0]} cursor="pointer"
                onClick={(bar) => bar?.payload?.month && openDrilldown(bar.payload.month)}/>
              <Line yAxisId="r" type="monotone" dataKey="rent_annual" name="Renta anual (€)" stroke="#f87171" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }}/>
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-white/40 text-sm py-10 text-center">Sin vencimientos registrados en los próximos {horizon} meses.</div>
        )}
      </div>

      {drilldown && (
        <div className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6" onClick={() => setDrilldown(null)} data-testid="forecast-drilldown">
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-5xl max-h-[95vh] overflow-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-white/10 sticky top-0 bg-[#0a0a0a] z-10">
              <div>
                <div className="text-xs font-black tracking-widest text-[#A3FF12]">VENCIMIENTOS · {drilldown.month}</div>
                <h2 className="text-lg sm:text-xl font-black text-white mt-0.5">
                  {drilldown.data ? `${drilldown.data.count} contratos · ${new Intl.NumberFormat('es-ES',{style:'currency',currency:'EUR',maximumFractionDigits:0}).format(drilldown.data.total_rent_annual || 0)}/año en riesgo` : 'Cargando…'}
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <ColumnPicker columns={FORECAST_COLUMNS} value={drillCols} onChange={setDrillCols} storageKey="forecast.drilldown.cols.v1"/>
                <button onClick={() => setDrilldown(null)} className="text-white/50 hover:text-white p-1.5"><X size={22}/></button>
              </div>
            </div>
            {drilldown.loading && (
              <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-white/40" size={28}/></div>
            )}
            {drilldown.error && <div className="p-6 text-red-300">{drilldown.error}</div>}
            {drilldown.data && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-white/50 uppercase text-[10px] border-b border-white/10">
                    <tr>
                      {activeDrillCols.map((c) => (
                        <th key={c.key} className={`px-3 py-2 ${c.align === 'right' ? 'text-right' : 'text-left'}`}>{c.label}</th>
                      ))}
                      <th/>
                    </tr>
                  </thead>
                  <tbody>
                    {groupByProvince(drilldown.data.items).map((group) => (
                      <React.Fragment key={group.province}>
                        <tr className="bg-white/[0.04] border-t border-white/10" data-testid={`drilldown-province-${group.province}`}>
                          <td colSpan={drillColSpan} className="px-3 py-2">
                            <div className="flex items-center gap-2 text-[11px] font-black tracking-widest text-[#A3FF12]">
                              <span className="uppercase">{group.province}</span>
                              <span className="text-white/40 font-mono">· {group.items.length}</span>
                            </div>
                          </td>
                        </tr>
                        {group.items.map((s) => (
                          <tr key={s.id} className="border-t border-white/5 hover:bg-white/5">
                            {activeDrillCols.map((c) => c.render(s))}
                            <td className="px-3 py-2 text-right">
                              <button onClick={() => { setDrilldown(null); navigate(`/app/emplazamientos?id=${s.id}`); }}
                                className="text-blue-300 hover:text-blue-100 font-bold text-[11px] flex items-center gap-1 ml-auto"
                                data-testid={`drill-edit-${s.id}`}>
                                <Edit2 size={12}/> Editar
                              </button>
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                    {drilldown.data.items.length === 0 && (
                      <tr><td colSpan={drillColSpan} className="text-center text-white/40 py-8">No hay contratos que venzan en {drilldown.month}.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Priority breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-10">
        <div className="bg-[#0a0a0a] border border-red-500/20 rounded-2xl p-5 flex items-center justify-between">
          <div>
            <div className="text-red-400 text-xs font-black tracking-widest">L1 · PRIORIDAD ALTA</div>
            <div className="text-3xl font-black text-white mt-1">{summary?.l1 ?? 0}</div>
            <div className="text-white/50 text-xs">Hay que llamar hoy</div>
          </div>
          <Phone className="text-red-400" size={32} strokeWidth={1.5}/>
        </div>
        <div className="bg-[#0a0a0a] border border-orange-500/20 rounded-2xl p-5 flex items-center justify-between">
          <div>
            <div className="text-orange-400 text-xs font-black tracking-widest">L2 · PRIORIDAD MEDIA</div>
            <div className="text-3xl font-black text-white mt-1">{summary?.l2 ?? 0}</div>
            <div className="text-white/50 text-xs">Esta semana</div>
          </div>
          <Clock className="text-orange-400" size={32} strokeWidth={1.5}/>
        </div>
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5 flex items-center justify-between">
          <div>
            <div className="text-white/60 text-xs font-black tracking-widest">L3 · PRIORIDAD BAJA</div>
            <div className="text-3xl font-black text-white mt-1">{summary?.l3 ?? 0}</div>
            <div className="text-white/50 text-xs">Seguimiento</div>
          </div>
          <FileText className="text-white/40" size={32} strokeWidth={1.5}/>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Coverage heatmap */}
        <div className="lg:col-span-2">
          <CoverageHeatmap/>
        </div>

        {/* Recent negotiations */}
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-black text-white">Negociaciones recientes</h2>
            <Link to="/app/negociaciones" className="text-[#A3FF12] text-sm font-bold hover:underline">Ver todo →</Link>
          </div>
          {negotiations.length === 0 ? (
            <div className="text-white/40 text-sm py-8 text-center">Aún no hay negociaciones. Crea una desde el módulo.</div>
          ) : (
            <div className="space-y-3">
              {negotiations.map((n) => (
                <div key={n.id} className="flex items-center justify-between gap-3 bg-black/40 border border-white/5 rounded-xl px-4 py-3">
                  <div className="min-w-0">
                    <div className="text-white font-bold truncate">{n.notes || `Negociación ${n.id.slice(0,8)}`}</div>
                    <div className="text-white/50 text-xs mt-0.5">Renta {n.current_rent}€ → Oferta {n.proposed_rent ?? '—'}€</div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <PriorityBadge p={n.priority}/>
                    <span className="text-white/40 text-xs uppercase">{n.status}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Alerts */}
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-black text-white">Avisos</h2>
            <span className="text-white/50 text-sm">{alerts.filter(a => !a.read).length} sin leer</span>
          </div>
          {alerts.length === 0 ? (
            <div className="text-white/40 text-sm py-8 text-center">Sin avisos por ahora.</div>
          ) : (
            <div className="space-y-3">
              {alerts.map((a) => (
                <div key={a.id} className={`flex items-start gap-3 rounded-xl px-4 py-3 border ${a.read ? 'bg-black/30 border-white/5' : 'bg-[#A3FF12]/5 border-[#A3FF12]/20'}`}>
                  <AlertTriangle size={16} className={a.read ? 'text-white/30 mt-1' : 'text-[#A3FF12] mt-1'}/>
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm font-bold">{a.title}</div>
                    <div className="text-white/50 text-xs mt-0.5">{a.message}</div>
                  </div>
                  <PriorityBadge p={a.priority}/>
                  {!a.read && (
                    <button onClick={() => markRead(a.id)} className="text-[#A3FF12] text-xs font-bold hover:underline">Leer</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
